import { useEffect, useMemo, useRef, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import { Box, Input, InputGroup as CInputGroup, InputLeftElement } from "@chakra-ui/react";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import type { InputProps } from "@chakra-ui/react";
import "./index.scss";

type Mode = "codigo" | "nome";
export type SearchPayload =
  | { mode: "codigo"; query: number | null }
  | { mode: "nome"; query: string };

type Props = {
  onSearch?: (p: SearchPayload) => void;
  debounceMs?: number;
};

export default function InputComSelect({ onSearch, debounceMs = 400 }: Props) {
  const [mode, setMode] = useState<Mode>("nome");
  const [query, setQuery] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const title = mode === "nome" ? "Nome" : "Código";
  const placeholder = mode === "nome" ? "Pesquisar por nome..." : "Pesquisar por código...";
  const type = mode === "nome" ? "text" : "number";

  const makePayload = (): SearchPayload => {
    if (mode === "codigo") {
      const n = query.trim() === "" ? null : Number(query);
      return { mode, query: Number.isNaN(n) ? null : n };
    }
    return { mode, query: query.trim() };
  };

  const submitNow = () => onSearch?.(makePayload());

  useEffect(() => {
    if (!onSearch) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch(makePayload()), debounceMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query, mode, debounceMs]);

  const inputProps = useMemo<Partial<InputProps>>(
    () =>
      mode === "codigo"
        ? { type: "number", inputMode: "numeric", pattern: "[0-9]*" }
        : { type: "text", inputMode: "text" },
    [mode]
  );

  return (
    <Box flex="1" minW="280px">
      <InputGroup className="w-100 mb-2">
        <DropdownButton
          id="input-group-dropdown"
          title={title}
          variant="outline-secondary"
          className="rb-prefix"
        >
          <Dropdown.Item as="button" onClick={() => setMode("nome")}>
            Nome
          </Dropdown.Item>
          <Dropdown.Item as="button" onClick={() => setMode("codigo")}>
            Código
          </Dropdown.Item>
        </DropdownButton>

        <CInputGroup flex="1" className="position-relative">
          <InputLeftElement pointerEvents="none">
            <AiOutlineSearch color="#6e6e6e" />
          </InputLeftElement>

          <Input
            className="rb-input chakra-like-input"
            bg="white"
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              let value = e.target.value;
              if (mode === "codigo" && value.length > 2) {
                value = value.slice(0, 2);
              }
              setQuery(value);
            }}
            maxLength={mode === "nome" ? 150 : 2}
            onKeyDown={(e) => e.key === "Enter" && submitNow()}
            _placeholder={{ color: "#6e6e6e" }}
            {...inputProps}
          />

          {query && (
            <Button
              variant="link"
              className="clear-btn p-0"
              aria-label="Limpar"
              onClick={() => setQuery("")}
              type="button"
            >
              <AiOutlineClose />
            </Button>
          )}
        </CInputGroup>
      </InputGroup>
    </Box>
  );
}
